/**
 * Maps extractor JSON key names (Excel-style headers) to purchase_orders API field names.
 * Kept in sync with main_v2.py column_mapping_header / column_mapping_lines.
 */
export const HEADER_LABEL_TO_DB = {
  'Customer PO #': 'customer_po_number',
  'Sub PO': 'sub_po',
  'Customer Distribution Channel': 'customer_distribution_channel',
  'Initial Order Date': 'initial_order_date',
  'PO Version No': 'po_version_number',
  'PO Version Date': 'po_version_date',
  'Bshipdate': 'bshipdate',
  'B Ship Date': 'bshipdate',
  'B Shipdate': 'bshipdate',
  'Handover Date': 'bshipdate',
  'DC DATE': 'dc_date',
  'DC Date': 'dc_date',
  'Dc Date': 'dc_date',
  'In DC Date': 'dc_date',
  'DC Receipt Date': 'dc_date',
  'MOD/Orig MOD': 'dc_date',
  'SHIP TO': 'ship_to',
  'Ship To': 'ship_to',
  'Brand': 'customer',
  'Brand ': 'customer',
  'Customer': 'customer',
  'Vendor Name/Number': 'vendor_name_number',
  'Vendor Name Number': 'vendor_name_number',
  'Vendor Name #': 'vendor_name_number',
  'Factory': 'factory',
  'COO': 'coo',
  'Origin Country': 'coo',
  'Origin Country:': 'coo',
  'Shipmode': 'shipmode',
  'Incoterm': 'incoterm',
  'Incoterm Origin': 'incoterm_origin',
  'PO Status': 'po_status',
  'Season': 'season',
  'Lane-pickup date/dont deliver before': 'lane_pickup_date_or_deliver_before',
  'Loft-Ticket Type': 'loft_ticket_type',
  'Ticket Type': 'loft_ticket_type',
  'Packaging': 'loft_ticket_type',
  'Customer Style Description': 'customer_style_description',
  'PO Number': 'po_number',
  'PO Date': 'po_date',
};

/** Line / grid coordinate labels → DB column on purchase_order_line_items */
export const LINE_LABEL_TO_DB = {
  'Customer Style Name': 'customer_style_name',
  'Customer Style #': 'customer_style_number',
  'Customer Style Description': 'customer_style_description',
  'Style/Article Description': 'customer_style_description',
  'Description': 'customer_style_name',
  'Article #': 'article_number',
  'Buyer Style #': 'customer_style_number',
  'Style/Article': 'customer_style_number',
  'Department': 'department',
  'Row #': 'row_number',
  'Pack method/type': 'pack_method_type',
  'Pack Method': 'pack_method_type',
  'Pack Type': 'pack_method_type',
  'Item Package Instruction': 'pack_method_type',
  'Color': 'color',
  'Secondary Color': 'secondary_color',
  'Size': 'size',
  'size': 'size',
  'UPC': 'upc',
  'Product ID': 'upc',
  'PCS to Ship ** This is total quantity of pcs)': 'pieces_to_ship',
  'Unit of Measure': 'unit_of_measure',
  'Pack Quantity': 'pack_quantity',
  'Qty of Prepacks': 'qty_of_prepacks',
  'Qty of Prepack': 'qty_of_prepacks',
  'Qty of Pre-packs': 'qty_of_prepacks',
  'US Retail Price': 'us_retail_price',
  'Sell Price': 'sell_price',
  'Total Row $ Value': 'total_row_dollar_value',
  'Total Row $ Value (pc * $)  ': 'total_row_dollar_value',
  'Prepack SKU': 'prepack_sku',
  'Prepack Article #': 'prepack_article_number',
  'Class Code': 'class_code',
  'HTS Description': 'hts_description',
  'VSS #': 'vss_number',
  'VSS Number': 'vss_number',
  'Vss Number': 'vss_number',
  'Lanebryant size': 'lanebryant_size',
  'Lane_bryant size': 'lanebryant_size',
  'Product Descr': 'customer_style_name',
  'Packaging': 'packaging',
  'LaneBryant:Vendor Style#': 'lanebryant_vendor_style_number',
  'AE Store type code': 'ae_store_type_code',
  'Free People-Vendor Style#': 'free_people_vendor_style_number',
  'Free People Vendor Style#': 'free_people_vendor_style_number',
  'Free People Vendor Style Number': 'free_people_vendor_style_number',
  'Free People-Vendor Style Number': 'free_people_vendor_style_number',
};

function normalizeFieldName(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/#/g, 'number')
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]/g, '');
}

function normalizeValueText(value) {
  const s = String(value ?? '').trim().toLowerCase();
  if (!s) return '';
  // Normalize common date delimiters and whitespace
  return s.replace(/[.-]/g, '/').replace(/\s+/g, '');
}

function toSnakeCase(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .replace(/[^a-zA-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_')
    .toLowerCase();
}

function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

function hasMeaningfulValue(obj, key) {
  if (!hasOwn(obj, key)) return false;
  const v = String(obj[key] ?? '').trim().toLowerCase();
  return !!v && !['not found', 'n/a', 'na', 'none', 'null', '—', '-'].includes(v);
}

function pickAliasKey(availableValues, candidates) {
  if (!availableValues) return null;
  for (const key of candidates) {
    if (hasMeaningfulValue(availableValues, key)) return key;
  }
  for (const key of candidates) {
    if (hasOwn(availableValues, key)) return key;
  }
  return null;
}

function resolveSpecialAlias(labelNorm, availableValues) {
  const aliases = {
    modorigmod: ['dc_date'],
    tickettype: ['loft_ticket_type'],
    packmethod: ['pack_method_type'],
    packtype: ['pack_method_type'],
    itempackageinstruction: ['pack_method_type'],
    buyerstylenumber: ['customer_style_number'],
    stylearticle: ['customer_style_number'],
    productdescr: ['customer_style_name'],
    productid: ['upc'],
    vendorstyle: ['free_people_vendor_style_number', 'prepack_article_number'],
  };
  return pickAliasKey(availableValues, aliases[labelNorm] || []);
}

/**
 * Resolve DB field key robustly across vendor label variations.
 * 1) explicit map lookup
 * 2) direct snake_case key from label
 * 3) normalized fuzzy match against available payload keys
 */
function resolveDbKey(label, explicitMap, availableValues) {
  if (!label) return null;

  const explicit = explicitMap[label];
  if (explicit) return explicit;

  // Try explicit mapping by normalized label to tolerate punctuation/spacing variants
  // like "Origin Country :", "Origin Country:", "Origin Country".
  const labelNorm = normalizeFieldName(label);
  const explicitNormMatch = Object.entries(explicitMap).find(
    ([k]) => normalizeFieldName(k) === labelNorm
  );
  if (explicitNormMatch?.[1]) return explicitNormMatch[1];

  const snake = toSnakeCase(label);
  if (snake && availableValues && Object.prototype.hasOwnProperty.call(availableValues, snake)) {
    return snake;
  }

  const targetNorm = normalizeFieldName(label);
  const specialAlias = resolveSpecialAlias(targetNorm, availableValues);
  if (specialAlias) return specialAlias;

  if (!availableValues) return null;
  const availableKeys = Object.keys(availableValues);
  const byNorm = Object.fromEntries(availableKeys.map((k) => [normalizeFieldName(k), k]));
  if (byNorm[labelNorm]) return byNorm[labelNorm];
  const exactNorm = availableKeys.find((k) => normalizeFieldName(k) === targetNorm);
  if (exactNorm) return exactNorm;

  // Small tolerance for prefixes/suffixes (e.g., "coo" vs "country_of_origin")
  const close = availableKeys.find((k) => {
    const nk = normalizeFieldName(k);
    return nk.includes(targetNorm) || targetNorm.includes(nk);
  });
  return close || null;
}

function pickBestValueRect(item, fieldValue, options = {}) {
  const { mergeFragments = true, fallbackToKey = true, preferredCenterY = null, rowBounds = null } = options;
  const allValues = item.values || [];
  const keyCoords = item.key?.coordinates;

  // Layer 1 — Row band constraint: restrict candidate values to those whose
  // center-Y falls within the row's bounding union (± tolerance). This prevents
  // values from adjacent rows in a shared grid from stealing the highlight.
  // If filtering leaves nothing we fall back to all values (safety net).
  const ROW_BAND_TOLERANCE = 20;
  const values = (() => {
    if (!rowBounds) return allValues;
    const inBand = allValues.filter((v) => {
      const c = v.coordinates;
      if (c?.x1 == null) return true;
      const cy = (c.y1 + c.y2) / 2;
      return cy >= rowBounds.y1 - ROW_BAND_TOLERANCE && cy <= rowBounds.y2 + ROW_BAND_TOLERANCE;
    });
    return inBand.length > 0 ? inBand : allValues;
  })();

  if (values.length === 0) {
    if (fallbackToKey && keyCoords && keyCoords.x1 != null) {
      return {
        x1: keyCoords.x1,
        y1: keyCoords.y1,
        x2: keyCoords.x2,
        y2: keyCoords.y2,
      };
    }
    return null;
  }
  const fv = fieldValue != null ? String(fieldValue).trim() : '';
  const norm = (s) => String(s || '').trim().replace(/\s+/g, ' ');
  const normLower = (s) => norm(s).toLowerCase();
  const fvLower = normLower(fv);
  if (['not found', 'n/a', 'na', 'none', 'null', '—', '-'].includes(fvLower)) {
    return null;
  }
  const digitsOnly = (s) => String(s || '').replace(/[^\d]/g, '');
  const area = (c) => Math.max(0, (c.x2 || 0) - (c.x1 || 0)) * Math.max(0, (c.y2 || 0) - (c.y1 || 0));
  const expand = (r, px = 3.5, py = 2.5) => ({
    x1: r.x1 - px,
    y1: r.y1 - py,
    x2: r.x2 + px,
    y2: r.y2 + py,
  });
  const union = (rectList) => {
    if (!rectList.length) return null;
    let minX = Infinity; let minY = Infinity; let maxX = -Infinity; let maxY = -Infinity;
    rectList.forEach((r) => {
      minX = Math.min(minX, r.x1);
      minY = Math.min(minY, r.y1);
      maxX = Math.max(maxX, r.x2);
      maxY = Math.max(maxY, r.y2);
    });
    if (minX === Infinity) return null;
    return { x1: minX, y1: minY, x2: maxX, y2: maxY };
  };
  const center = (r) => ({ x: (r.x1 + r.x2) / 2, y: (r.y1 + r.y2) / 2 });
  const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

  // Layer 2 — Short-value key-proximity guard: for very short values (≤ 3 chars,
  // e.g. "0", "1", "XS") that appear many times across a PDF, require candidates
  // to be within a tight radius of the key label to avoid false matches on stray
  // numbers (page numbers, totals, etc.).
  const SHORT_VAL_THRESHOLD = 3;
  const SHORT_VAL_KEY_RADIUS = 220;
  const keyCenter = keyCoords?.x1 != null ? center(keyCoords) : null;
  const isShortAmbiguous = fv.length <= SHORT_VAL_THRESHOLD && keyCenter != null;

  // Multi-line / fragmented values: merge all matching fragments into one complete highlight.
  if (mergeFragments && fvLower && values.length > 1) {
    const tokens = Array.from(
      new Set(
        fvLower
          .split(/[^a-z0-9]+/i)
          .map((t) => t.trim())
          .filter((t) => t.length >= 2)
      )
    );
    const fragmentRects = [];
    for (const v of values) {
      const c = v.coordinates;
      if (c?.x1 == null) continue;
      // Layer 2 filter for merge-fragments path
      if (isShortAmbiguous && distance(center({ x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2 }), keyCenter) > SHORT_VAL_KEY_RADIUS) continue;
      const raw = String(v.value ?? '');
      const valLower = normLower(raw);
      if (!valLower) continue;

      const overlaps = tokens.reduce((acc, t) => (valLower.includes(t) ? acc + 1 : acc), 0);
      const overlapRatio = tokens.length ? overlaps / tokens.length : 0;
      const isSub = fv.length > 2 && (fvLower.includes(valLower) || valLower.includes(fvLower));
      if (isSub || overlapRatio >= 0.5) {
        fragmentRects.push({ x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2 });
      }
    }
    if (fragmentRects.length > 0) {
      if (fragmentRects.length === 1) return expand(fragmentRects[0]);

      const clusters = [];
      const sortedRects = [...fragmentRects].sort((a, b) => a.y1 - b.y1);
      sortedRects.forEach((rect) => {
        // Group fragments that are vertically close to ANY rect in the cluster
        const cluster = clusters.find((c) =>
          c.rects.some(r => Math.abs(center(r).y - center(rect).y) <= 32)
        );
        if (cluster) {
          cluster.rects.push(rect);
          cluster.centerY = cluster.rects.reduce((sum, r) => sum + center(r).y, 0) / cluster.rects.length;
        } else {
          clusters.push({ centerY: center(rect).y, rects: [rect] });
        }
      });

      let bestCluster;
      if (preferredCenterY != null) {
        bestCluster = clusters.sort(
          (a, b) => Math.abs(a.centerY - preferredCenterY) - Math.abs(b.centerY - preferredCenterY)
        )[0];
      } else {
        if (keyCenter) {
          bestCluster = clusters.sort(
            (a, b) => Math.abs(a.centerY - keyCenter.y) - Math.abs(b.centerY - keyCenter.y)
          )[0];
        } else {
          bestCluster = clusters[0];
        }
      }

      const mergedCluster = union(bestCluster?.rects || []);
      if (mergedCluster) return expand(mergedCluster);
    }
  }

  let best = null;
  const matched = [];
  const tokens = Array.from(new Set(fvLower.split(/[^a-z0-9]+/i).map((t) => t.trim()).filter((t) => t.length >= 2)));

  for (const v of values) {
    const c = v.coordinates;
    if (c?.x1 == null) continue;
    // Layer 2 filter: short ambiguous values must be near the key label
    if (isShortAmbiguous && distance(center({ x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2 }), keyCenter) > SHORT_VAL_KEY_RADIUS) continue;
    const raw = String(v.value ?? '');
    const valLower = normLower(raw);
    if (!fvLower || !valLower) continue;

    let score = 0;
    const overlaps = tokens.reduce((acc, t) => (valLower.includes(t) ? acc + 1 : acc), 0);
    const overlapRatio = tokens.length ? overlaps / tokens.length : 0;

    if (valLower === fvLower) {
      score = 100;
    } else if (digitsOnly(valLower) && digitsOnly(valLower) === digitsOnly(fvLower)) {
      score = 90;
    } else if (overlapRatio >= 0.7) {
      score = 80;
    } else if (fv.length > 2 && (fvLower.includes(valLower) || valLower.includes(fvLower))) {
      // Substring inclusion only for values longer than 2 chars — prevents single
      // digits like "0" or "1" from matching dates, quantities, etc. as substrings.
      score = 60;
    } else if (overlapRatio >= 0.4) {
      score = 40;
    }

    if (score === 0) continue;

    const rect = { x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2 };
    const candidate = {
      rect,
      score,
      area: area(c),
      rowDistance: preferredCenterY != null ? Math.abs(center(rect).y - preferredCenterY) : Infinity,
      distance: keyCenter ? distance(center(rect), keyCenter) : Infinity,
    };
    matched.push(candidate);
    if (
      !best
      || candidate.score > best.score
      || (
        candidate.score === best.score
        && candidate.rowDistance < best.rowDistance
      )
      || (candidate.score === best.score && candidate.distance < best.distance)
      || (
        candidate.score === best.score
        && candidate.rowDistance === best.rowDistance
        && candidate.distance === best.distance
        && candidate.area < best.area
      )
    ) {
      best = candidate;
    }
  }
  if (best?.rect) {
    if (mergeFragments) return expand(best.rect);

    // Header mode: keep one occurrence only, but merge nearby fragments that
    // belong to the same textual value (prevents one-word highlight).
    if (matched.length > 1) {
      let anchor = best;
      if (keyCenter) {
        const ranked = [...matched].sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          const da = distance(center(a.rect), keyCenter);
          const db = distance(center(b.rect), keyCenter);
          if (da !== db) return da - db;
          return a.area - b.area;
        });
        anchor = ranked[0];
      }

      const a = anchor.rect;
      const lineH = Math.max(8, a.y2 - a.y1);
      const near = matched.filter((m) => {
        const r = m.rect;
        const verticalOverlap = Math.min(a.y2, r.y2) - Math.max(a.y1, r.y1);
        const sameLine = verticalOverlap > -lineH * 0.35 || Math.abs(r.y1 - a.y1) <= lineH * 0.7;
        const nearX = r.x1 <= a.x2 + 90 && r.x2 >= a.x1 - 40;
        const keyOk = !keyCenter || distance(center(r), keyCenter) <= 280;
        return m.score >= 60 && sameLine && nearX && keyOk;
      });
      const merged = union(near.map((m) => m.rect));
      if (merged) return expand(merged);
    }
    return expand(best.rect);
  }

  // When value matching is uncertain, prefer the key box to avoid highlighting
  // an unrelated value block.
  if (fallbackToKey && keyCoords?.x1 != null) {
    return expand({ x1: keyCoords.x1, y1: keyCoords.y1, x2: keyCoords.x2, y2: keyCoords.y2 });
  }

  // If we cannot match by value, prefer the box closest to the preferred row or key center
  let fallbackBox = null;
  for (const v of values) {
    const c = v.coordinates;
    if (c?.x1 == null) continue;
    const boxCenter = center({ x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2 });
    
    let dist = Infinity;
    if (preferredCenterY != null) {
      dist = Math.abs(boxCenter.y - preferredCenterY);
    } else if (keyCenter) {
      dist = distance(boxCenter, keyCenter);
    } else {
      dist = area(c); // fallback to smallest if no anchors
    }
    
    if (!fallbackBox || dist < fallbackBox.dist) {
      fallbackBox = { dist, rect: { x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2 } };
    }
  }
  return fallbackBox?.rect ? expand(fallbackBox.rect) : null;
}

function unionValueRects(item) {
  const values = item?.values || [];
  const valid = values
    .map((v) => v?.coordinates)
    .filter((c) => c?.x1 != null && c?.y1 != null && c?.x2 != null && c?.y2 != null);
  if (!valid.length) return null;
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  valid.forEach((c) => {
    minX = Math.min(minX, c.x1);
    minY = Math.min(minY, c.y1);
    maxX = Math.max(maxX, c.x2);
    maxY = Math.max(maxY, c.y2);
  });
  return { x1: minX, y1: minY, x2: maxX, y2: maxY };
}

function rectCenterY(rect) {
  return rect ? (rect.y1 + rect.y2) / 2 : Infinity;
}

function pickRowAnchorY(candidates, rowValues) {
  const anchorWeights = {
    row_number: 9,
    customer_style_number: 8,
    article_number: 8,
    upc: 8,
    free_people_vendor_style_number: 8,
    lanebryant_vendor_style_number: 8,
    prepack_article_number: 7,
    customer_style_name: 6,
    customer_style_description: 6,
    color: 5,
    secondary_color: 4,
    size: 4,
    pack_method_type: 3,
    pieces_to_ship: 3,
  };
  const usable = candidates.filter(
    (candidate) => anchorWeights[candidate.dbKey] && hasMeaningfulValue(rowValues, candidate.dbKey)
  );
  if (!usable.length) return null;

  const tolerance = 20;
  let best = null;
  for (const candidate of usable) {
    const centerY = rectCenterY(candidate.rect);
    let clusterWeight = 0;
    for (const other of usable) {
      if (Math.abs(rectCenterY(other.rect) - centerY) <= tolerance) {
        clusterWeight += anchorWeights[other.dbKey] || 1;
      }
    }
    const ownWeight = anchorWeights[candidate.dbKey] || 1;
    if (
      !best
      || clusterWeight > best.clusterWeight
      || (clusterWeight === best.clusterWeight && ownWeight > best.ownWeight)
    ) {
      best = { centerY, clusterWeight, ownWeight };
    }
  }
  return best?.centerY ?? null;
}

function choosePreferredLineCandidate(existing, candidate, pageAnchors) {
  if (!existing) return candidate;

  const existingAnchorY = pageAnchors[existing.page];
  const candidateAnchorY = pageAnchors[candidate.page];
  const existingRowDist = existingAnchorY == null ? Infinity : Math.abs(rectCenterY(existing.rect) - existingAnchorY);
  const candidateRowDist = candidateAnchorY == null ? Infinity : Math.abs(rectCenterY(candidate.rect) - candidateAnchorY);

  if (candidateRowDist !== existingRowDist) {
    return candidateRowDist < existingRowDist ? candidate : existing;
  }

  const existingKeyDist = existing.keyRect ? Math.abs(rectCenterY(existing.rect) - rectCenterY(existing.keyRect)) : Infinity;
  const candidateKeyDist = candidate.keyRect ? Math.abs(rectCenterY(candidate.rect) - rectCenterY(candidate.keyRect)) : Infinity;
  if (candidateKeyDist !== existingKeyDist) {
    return candidateKeyDist < existingKeyDist ? candidate : existing;
  }

  const existingArea = Math.max(0, (existing.rect.x2 - existing.rect.x1) * (existing.rect.y2 - existing.rect.y1));
  const candidateArea = Math.max(0, (candidate.rect.x2 - candidate.rect.x1) * (candidate.rect.y2 - candidate.rect.y1));
  if (candidateArea !== existingArea) {
    return candidateArea < existingArea ? candidate : existing;
  }

  return candidate.page < existing.page ? candidate : existing;
}

function estimateLineItemRect(item, pageItems, pageCandidates, anchorY) {
  const keyRect = item?.key?.coordinates;
  if (!keyRect || keyRect.x1 == null || keyRect.y1 == null || anchorY == null) return null;

  const nearbyRects = pageCandidates
    .map((candidate) => candidate.rect)
    .filter((rect) => Math.abs(rectCenterY(rect) - anchorY) <= 22);

  let rowY1 = Infinity;
  let rowY2 = -Infinity;
  nearbyRects.forEach((rect) => {
    rowY1 = Math.min(rowY1, rect.y1);
    rowY2 = Math.max(rowY2, rect.y2);
  });

  if (!nearbyRects.length) {
    rowY1 = keyRect.y2 + 2;
    rowY2 = rowY1 + Math.max(10, (keyRect.y2 - keyRect.y1) * 1.8);
  } else {
    rowY1 -= 2.5;
    rowY2 += 2.5;
  }

  const keyRects = pageItems
    .map((pageItem) => pageItem?.key?.coordinates)
    .filter((rect) => rect?.x1 != null)
    .sort((a, b) => a.x1 - b.x1);

  const nextX = keyRects.find((rect) => rect.x1 > keyRect.x1 + 1)?.x1 ?? null;
  const columnGap = nextX != null ? nextX - keyRect.x1 : null;
  const leftPad = columnGap != null ? Math.min(16, Math.max(8, columnGap * 0.18)) : 12;
  const rightPad = nextX != null ? Math.min(8, Math.max(4, columnGap * 0.08)) : 28;
  const estimatedX2 =
    nextX != null
      ? Math.max(keyRect.x2 + 12, nextX - rightPad)
      : keyRect.x2 + Math.max(36, (keyRect.x2 - keyRect.x1) * 2.2);

  return {
    x1: Math.max(0, keyRect.x1 - leftPad),
    y1: rowY1,
    x2: estimatedX2,
    y2: rowY2,
  };
}

function parseLegacyPageFields(page, intoMap, availableValues, explicitMap) {
  if (!page?.fields) return;
  const p = page.page || 1;
  Object.entries(page.fields).forEach(([fieldName, fieldData]) => {
    const x = fieldData.x ?? fieldData.left;
    const y = fieldData.y ?? fieldData.top;
    if (x == null || y == null) return;
    const w = fieldData.width
      ?? (fieldData.right != null && fieldData.left != null ? fieldData.right - fieldData.left : 80);
    const h = fieldData.height
      ?? (fieldData.bottom != null && fieldData.top != null ? fieldData.bottom - fieldData.top : 16);
    const pw = fieldData.pageWidth || 595;
    const ph = fieldData.pageHeight || 842;
    const rect = {
      page: p,
      x1: x,
      y1: y,
      x2: x + w,
      y2: y + h,
      _pageW: pw,
      _pageH: ph,
    };
    intoMap[fieldName] = rect;
    const resolved = resolveDbKey(fieldName, explicitMap || {}, availableValues || {});
    if (resolved) intoMap[resolved] = rect;
  });
}

/**
 * Header-level rects keyed by API field name (e.g. customer_po_number).
 */
export function buildHeaderFieldCoordinateMap(header_coordinates, editedData, supplementalCoordinates = null) {
  const map = {};
  const payloads = [header_coordinates, supplementalCoordinates].filter(Boolean);
  if (!payloads.length) return map;
  const seenItems = [];

  payloads.forEach((payload, payloadIndex) => {
    let obj = payload;
    if (typeof obj === 'string') {
      try {
        obj = JSON.parse(obj);
      } catch {
        return;
      }
    }

    if (!obj?.pages || !Array.isArray(obj.pages)) return;

    for (const page of obj.pages) {
      parseLegacyPageFields(page, map, editedData, HEADER_LABEL_TO_DB);
      const p = page.page || 1;
      const items = page.coordinates || [];
      for (const item of items) {
        seenItems.push({ item, page: p });
        const keyName = item.key?.name;
        if (!keyName) continue;
        const dbKey = resolveDbKey(keyName, HEADER_LABEL_TO_DB, editedData);
        if (!dbKey) continue;
        if (payloadIndex > 0 && map[dbKey]) continue;
        // Prefer deterministic key-linked value region over text matching
        // to avoid misses on dates like bshipdate/dc_date.
        // Header fields should highlight one target box only, even if value repeats.
        // Never fall back to the key/label coordinates — that would highlight the
        // column header text instead of the actual value.  Use unionValueRects as
        // a secondary fallback so multi-word values stored word-by-word (e.g.
        // Chico's "Style/Article Description" zone search) still get a rect that
        // covers the full value area rather than a single matched word.
        const WIDE_HEADER_FIELDS = new Set(['ship_to', 'vendor_name_number', 'customer_style_description', 'factory']);
        const rect = pickBestValueRect(item, editedData?.[dbKey], {
          mergeFragments: WIDE_HEADER_FIELDS.has(dbKey),
          fallbackToKey: false,
        }) || unionValueRects(item);
        if (!rect) continue;
        map[dbKey] = { ...rect, page: p };
      }
    }
  });

  // Vendor-agnostic fallback: for any unresolved header field, match by value
  // against all header coordinate blocks and choose the tightest best score.
  if (editedData && seenItems.length > 0) {
    const area = (r) => Math.max(0, (r.x2 || 0) - (r.x1 || 0)) * Math.max(0, (r.y2 || 0) - (r.y1 || 0));
    Object.keys(editedData).forEach((dbKey) => {
      if (map[dbKey]) return;
      const wanted = normalizeValueText(editedData[dbKey]);
      if (!wanted || ['notfound', 'n/a', 'na', 'none', 'null', '—', '-'].includes(wanted)) return;
      // Purely-numeric short values (version numbers, single quantities like "0", "1", "2")
      // cannot be safely matched without explicit coordinate context — they collide with
      // prices, quantities, page numbers, and other numeric data across the PDF.
      // Skip them here; they will show no highlight rather than a wrong one.
      if (/^\d{1,3}$/.test(wanted)) return;

      let best = null;
      for (const { item, page } of seenItems) {
        for (const v of item.values || []) {
          const c = v?.coordinates;
          if (!c?.x1 && c?.x1 !== 0) continue;
          const got = normalizeValueText(v.value);
          if (!got) continue;

          let score = 0;
          if (got === wanted) score = 100;
          else if (wanted.length > 2 && (got.includes(wanted) || wanted.includes(got))) score = 70;
          else {
            const gotDigits = got.replace(/[^\d]/g, '');
            const wantedDigits = wanted.replace(/[^\d]/g, '');
            if (gotDigits && wantedDigits && gotDigits === wantedDigits) score = 85;
          }
          if (!score) continue;

          const rect = { x1: c.x1, y1: c.y1, x2: c.x2, y2: c.y2, page };
          const candidate = { score, rect, area: area(rect) };
          if (!best || candidate.score > best.score || (candidate.score === best.score && candidate.area < best.area)) {
            best = candidate;
          }
        }
      }
      if (best?.rect) {
        map[dbKey] = best.rect;
      }
    });
  }

  return map;
}

/**
 * Per line-item rects from item_coordinates JSON (aligned row payload).
 */
export function buildLineItemFieldRects(item_coordinates, rowValues) {
  const rects = {};
  if (!item_coordinates) return rects;

  let parsed = item_coordinates;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return rects;
    }
  }

  if (!parsed.pages || !Array.isArray(parsed.pages)) return rects;

  // Layer 3 — Compute row bounding union from the stored coordinates.
  // This is the same region that lineItemUnionRect() draws for the row-hover highlight.
  // Using it as a spatial constraint replaces the heuristic pickRowAnchorY anchor for
  // the initial first-pass anchor candidates, and is passed into pickBestValueRect as
  // rowBounds so cross-row value occurrences are excluded before scoring.
  const pageRowBounds = {};
  for (const pg of parsed.pages) {
    const p = pg.page || 1;
    let minY = Infinity; let maxY = -Infinity;
    for (const item of pg.coordinates || []) {
      for (const v of item.values || []) {
        const c = v.coordinates;
        if (c?.x1 == null) continue;
        minY = Math.min(minY, c.y1);
        maxY = Math.max(maxY, c.y2);
      }
    }
    if (minY < Infinity) pageRowBounds[p] = { y1: minY, y2: maxY };
  }

  const anchorCandidates = [];
  for (const pg of parsed.pages) {
    const p = pg.page || 1;
    const rowBounds = pageRowBounds[p] ?? null;
    for (const item of pg.coordinates || []) {
      const keyName = item.key?.name;
      if (!keyName) continue;
      const dbKey = resolveDbKey(keyName, LINE_LABEL_TO_DB, rowValues);
      if (!dbKey) continue;
      const rawVal = rowValues?.[dbKey];
      // Skip fields with no meaningful value so they don't contribute a bogus anchor
      // (e.g. FreePeople's prepack_article_number when vendor style lives elsewhere).
      const rawStr = String(rawVal ?? '').trim().toLowerCase();
      if (!rawStr || ['not found', 'n/a', 'na', 'none', 'null', '—', '-'].includes(rawStr)) continue;
      const rect = pickBestValueRect(item, rawVal, {
        mergeFragments: false,
        fallbackToKey: false,
        rowBounds,
      }) || unionValueRects(item);
      if (!rect) continue;
      anchorCandidates.push({
        dbKey,
        page: p,
        rect: { ...rect, page: p },
      });
    }
  }

  const pageAnchors = Object.fromEntries(
    parsed.pages
      .map((pg) => {
        const p = pg.page || 1;
        // Prefer the union-rect center as anchor Y — it's deterministic and matches
        // the row-hover highlight. Fall back to the weighted-cluster estimate only
        // when no union bounds are available.
        if (pageRowBounds[p]) {
          const { y1, y2 } = pageRowBounds[p];
          return [p, (y1 + y2) / 2];
        }
        return [p, pickRowAnchorY(anchorCandidates.filter((candidate) => candidate.page === p), rowValues)];
      })
      .filter(([, anchorY]) => anchorY != null)
  );

  const candidates = [];
  for (const pg of parsed.pages) {
    const p = pg.page || 1;
    const pageItems = pg.coordinates || [];
    const rowBounds = pageRowBounds[p] ?? null;
    for (const item of pg.coordinates || []) {
      const keyName = item.key?.name;
      if (!keyName) continue;
      const dbKey = resolveDbKey(keyName, LINE_LABEL_TO_DB, rowValues);
      if (!dbKey) continue;
      const rawVal = rowValues?.[dbKey];
      // Skip empty / placeholder values so we never highlight a cell that has no data
      // (e.g. FreePeople prepack_article_number when it's blank for this row).
      const textVal = String(rawVal ?? '').trim();
      const textLower = textVal.toLowerCase();
      if (!textVal || ['not found', 'n/a', 'na', 'none', 'null', '—', '-'].includes(textLower)) continue;
      // shortCell: only suppress fragment-merging for truly single-token short values.
      // A lower threshold prevents "half-value highlighted" when the extractor splits
      // multi-word values across several text fragments.
      const shortCell = textVal.length <= 12 && textVal.split(/\s+/).length <= 1;
      const needsFullSpan = ['customer_style_description', 'ship_to'].includes(dbKey);
      const rect =
        pickBestValueRect(item, rawVal, {
          mergeFragments: needsFullSpan || !shortCell,
          fallbackToKey: false,
          preferredCenterY: pageAnchors[p] ?? null,
          rowBounds,
        }) ||
        unionValueRects(item);
      candidates.push({
        dbKey,
        page: p,
        rect: rect ? { ...rect, page: p } : null,
        keyRect: item.key?.coordinates || null,
        item,
        pageItems,
      });
    }
  }

  const chosen = {};
  for (const candidate of candidates) {
    const anchorY = pageAnchors[candidate.page];
    const finalizedRect = candidate.rect || estimateLineItemRect(
      candidate.item,
      candidate.pageItems,
      candidates.filter((pageCandidate) => pageCandidate.page === candidate.page && pageCandidate.rect),
      anchorY
    );
    if (!finalizedRect) continue;
    const enrichedCandidate = {
      ...candidate,
      rect: { ...finalizedRect, page: candidate.page },
    };
    chosen[candidate.dbKey] = choosePreferredLineCandidate(chosen[candidate.dbKey], enrichedCandidate, pageAnchors);
  }

  Object.values(chosen).forEach((candidate) => {
    rects[candidate.dbKey] = candidate.rect;
  });

  // Fallback: if a field key exists in row values but we couldn't map a rect for it,
  // try matching by normalized key-name against any already extracted rect key.
  if (rowValues && Object.keys(rects).length > 0) {
    const relatedFieldFallbacks = {
      free_people_vendor_style_number: ['prepack_article_number'],
    };

    Object.entries(relatedFieldFallbacks).forEach(([target, aliases]) => {
      if (rects[target]) return;
      const fallbackKey = aliases.find((alias) => rects[alias]);
      if (fallbackKey) rects[target] = rects[fallbackKey];
    });

    const rectKeys = Object.keys(rects);
    for (const rk of Object.keys(rowValues)) {
      if (rects[rk]) continue;
      const normRk = normalizeFieldName(rk);
      const close = rectKeys.find((k) => {
        const nk = normalizeFieldName(k);
        if (nk === normRk) return true;
        // Only allow substring matching when BOTH strings are long enough (≥ 6 chars)
        // to prevent short generic keys like 'size' from spuriously matching longer
        // vendor-specific keys like 'lanebryant_size'.
        const minLen = Math.min(nk.length, normRk.length);
        if (minLen < 6) return false;
        return nk.includes(normRk) || normRk.includes(nk);
      });
      if (close) rects[rk] = rects[close];
    }
  }

  return rects;
}

/** Bounding union of all value rects in a line item (row hover fallback). */
export function lineItemUnionRect(item_coordinates) {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let page = 1;

  let parsed = item_coordinates;
  if (!parsed) return null;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return null;
    }
  }

  if (!parsed.pages) return null;

  for (const pg of parsed.pages) {
    page = pg.page || 1;
    for (const item of pg.coordinates || []) {
      for (const v of item.values || []) {
        const c = v.coordinates;
        if (!c || c.x1 == null) continue;
        minX = Math.min(minX, c.x1);
        minY = Math.min(minY, c.y1);
        maxX = Math.max(maxX, c.x2);
        maxY = Math.max(maxY, c.y2);
      }
      const kc = item.key?.coordinates;
      if (kc?.x1 != null) {
        minX = Math.min(minX, kc.x1);
        minY = Math.min(minY, kc.y1);
        maxX = Math.max(maxX, kc.x2);
        maxY = Math.max(maxY, kc.y2);
      }
    }
  }

  if (minX === Infinity) return null;
  return { page, x1: minX, y1: minY, x2: maxX, y2: maxY };
}

/**
 * Convert extractor / PyMuPDF-style rect (origin top-left, y increases down) to CSS pixels
 * for a react-pdf canvas at the given scale. PDF.js user space uses bottom-left origin.
 */
export function fitzRectToCssOverlay(fitzRect, pageProxy, scale) {
  if (!fitzRect || !pageProxy || fitzRect.x1 == null) return null;
  const rotation = pageProxy.rotate ?? 0;
  const viewport = pageProxy.getViewport({ scale, rotation });
  const pageHeight = viewport.rawDims.pageHeight;
  const pdfRect = [
    fitzRect.x1,
    pageHeight - fitzRect.y2,
    fitzRect.x2,
    pageHeight - fitzRect.y1,
  ];
  const [vx1, vy1, vx2, vy2] = viewport.convertToViewportRectangle(pdfRect);
  const left = Math.min(vx1, vx2);
  const top = Math.min(vy1, vy2);
  const width = Math.abs(vx2 - vx1);
  const height = Math.abs(vy2 - vy1);
  if (width < 1 || height < 1) return null;
  // Add a tiny visual pad so full glyphs are covered.
  const padX = 3;
  const padY = 2;
  return { left: left - padX, top: top - padY, width: width + 2 * padX, height: height + 2 * padY };
}
